/**
 * Board #215 (frontend half) — everything the claim documents panel needs.
 *
 * The BACKEND half shipped already: `ClaimDocumentViewSet.perform_create` now
 * stores the upload through `default_storage` and records the path it actually
 * wrote, `verify` sets `status` (the field the model really has) instead of the
 * `is_verified` attribute that Django silently discarded, and `download`
 * streams the stored bytes back. This module is written against THAT API, not
 * against what the old code pretended to do.
 *
 * WHY THIS LIVES UNDER pages/claims/ AND NOT services/claims.ts
 * -------------------------------------------------------------
 * `services/claims.ts` is outside this task's file domain. Two of the calls
 * below (`verify`, `reject`, `download`) do not exist there at all, and one that
 * does — `getDocuments` — is typed `Promise<ClaimDocument[]>` while the endpoint
 * is paginated and actually returns `{count, results}`. That mismatch is why the
 * Documents card said "No documents uploaded yet." no matter how many rows
 * existed: `documents.length` on the envelope is `undefined`. `readList` below
 * accepts either shape. See the handover note about folding this back into
 * `services/claims.ts`.
 */

import api from '@/services/api';

/** One row of `GET /claims/{id}/documents/`, as `ClaimDocumentSerializer` sends it. */
export interface ClaimDocumentRow {
  id: string;
  document_type: string;
  name: string;
  /** Storage path the server wrote. NOT a URL — never render it as an href. */
  file_path: string;
  file_size: number | null;
  mime_type: string;
  /** 'pending' | 'approved' | 'rejected' | 'resubmit' — ClaimDocument.STATUS_CHOICES. */
  status: string;
  status_display?: string;
  verified_by_name?: string;
  verified_at?: string | null;
  uploaded_by: string | null;
  uploaded_by_name?: string;
  rejection_reason?: string;
  is_mandatory?: boolean;
  created_at: string;
}

/**
 * Mirrors `ClaimDocumentSerializer.ALLOWED_UPLOAD_TYPES` /
 * `MAX_UPLOAD_BYTES` in backend/apps/claims/serializers.py.
 *
 * Checking here is a courtesy, not a gate: the server re-validates every
 * upload, and it is the server's answer that decides. The point is to fail in
 * the file picker rather than after a 10 MB round trip.
 */
export const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;

/** `MAX_VIDEO_UPLOAD_BYTES`: phone clips are bigger than scans. */
export const MAX_VIDEO_UPLOAD_BYTES = 25 * 1024 * 1024;

/** `VIDEO_EXTENSIONS`. The server takes video only as damage photos or other. */
export const VIDEO_EXTENSIONS = ['.mp4', '.m4v', '.mov', '.webm'];

export const ALLOWED_UPLOAD_EXTENSIONS = [
  '.pdf',
  '.jpg',
  '.jpeg',
  '.png',
  '.webp',
  '.heic',
  '.heif',
  '.tif',
  '.tiff',
  '.doc',
  '.docx',
  '.xls',
  '.xlsx',
  ...VIDEO_EXTENSIONS,
];

/** `accept` attribute for the file input — the same list, comma separated. */
export const UPLOAD_ACCEPT_ATTRIBUTE = ALLOWED_UPLOAD_EXTENSIONS.join(',');

export const SUPPORTED_TYPES_MESSAGE =
  'Upload a PDF, a photo (JPG, PNG, WEBP, HEIC or TIFF), a short video (MP4, MOV or WEBM), ' +
  'or a Word/Excel document.';

function extensionOf(fileName: string): string {
  const dot = fileName.lastIndexOf('.');
  return dot === -1 ? '' : fileName.slice(dot).toLowerCase();
}

export function formatFileSize(bytes: number | null | undefined): string {
  if (!bytes || bytes <= 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * The reason this file cannot be uploaded, or `null` if it can.
 *
 * Returns the reason rather than a boolean so the caller can put the actual
 * problem in front of the user — "that file is 14.2 MB" beats "invalid file".
 */
export function validateClaimDocumentFile(file: File): string | null {
  if (!file.size) {
    return `${file.name} is empty.`;
  }
  const extension = extensionOf(file.name);
  const limit = VIDEO_EXTENSIONS.includes(extension) ? MAX_VIDEO_UPLOAD_BYTES : MAX_UPLOAD_BYTES;
  if (file.size > limit) {
    return `${file.name} is ${formatFileSize(file.size)}. The limit is ${limit / 1048576} MB.`;
  }
  if (!ALLOWED_UPLOAD_EXTENSIONS.includes(extension)) {
    return `${extension || 'That file type'} is not a supported document. ${SUPPORTED_TYPES_MESSAGE}`;
  }
  return null;
}

/** `ClaimDocument.DOCUMENT_TYPE_CHOICES`, backend/apps/claims/models.py. */
export const CLAIM_DOCUMENT_TYPES: { value: string; label: string }[] = [
  { value: 'claim_form', label: 'Claim Form' },
  { value: 'id_proof', label: 'ID Proof' },
  { value: 'policy_copy', label: 'Policy Copy' },
  { value: 'driving_license', label: 'Driving License' },
  { value: 'rc_copy', label: 'RC Copy' },
  { value: 'fir_copy', label: 'FIR Copy' },
  { value: 'repair_estimate', label: 'Repair Estimate' },
  { value: 'repair_invoice', label: 'Repair Invoice' },
  { value: 'damage_photos', label: 'Damage Photos' },
  { value: 'survey_report', label: 'Survey Report' },
  { value: 'discharge_summary', label: 'Discharge Summary' },
  { value: 'hospital_bills', label: 'Hospital Bills' },
  { value: 'prescription', label: 'Prescription' },
  { value: 'diagnostic_reports', label: 'Diagnostic Reports' },
  { value: 'pre_auth_form', label: 'Pre-Authorization Form' },
  // DQ-346: reimbursement slots that used to be "other" plus a name.
  { value: 'pharmacy_bills', label: 'Pharmacy Bills' },
  { value: 'payment_receipts', label: 'Payment Receipts' },
  { value: 'bank_details', label: 'Cancelled Cheque / Bank Details' },
  { value: 'other', label: 'Other' },
];

const DOCUMENT_TYPE_LABELS = new Map(CLAIM_DOCUMENT_TYPES.map((t) => [t.value, t.label]));

export function documentTypeLabel(value: string): string {
  return (
    DOCUMENT_TYPE_LABELS.get(value) ??
    value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
  );
}

/**
 * The standard document set for each `Claim.CLAIM_TYPE_CHOICES` value.
 *
 * HONESTY NOTE, because this is the kind of list that gets mistaken for a
 * server-side rule: this is a CLIENT-SIDE checklist. `apps.claims` exposes no
 * per-claim-type requirement anywhere (`ClaimDocument.is_mandatory` is a flag on
 * an already-uploaded row, not a list of what is expected), and this task may
 * not add one — `backend/apps/claims/**` is another agent's file domain. So the
 * panel that renders this says "standard checklist" and warns that the insurer
 * may ask for more; it never claims the claim is complete. Moving this list to
 * the API is the follow-up recorded in the handover.
 */
export const REQUIRED_DOCUMENTS_BY_CLAIM_TYPE: Record<string, string[]> = {
  own_damage: ['claim_form', 'policy_copy', 'driving_license', 'rc_copy', 'repair_estimate', 'damage_photos'],
  third_party: ['claim_form', 'policy_copy', 'driving_license', 'rc_copy', 'fir_copy'],
  theft: ['claim_form', 'policy_copy', 'fir_copy', 'rc_copy', 'id_proof'],
  total_loss: ['claim_form', 'policy_copy', 'fir_copy', 'rc_copy', 'survey_report'],
  partial_loss: ['claim_form', 'policy_copy', 'rc_copy', 'repair_estimate', 'damage_photos'],
  cashless: ['claim_form', 'policy_copy', 'id_proof', 'pre_auth_form', 'prescription'],
  reimbursement: [
    'claim_form',
    'policy_copy',
    'id_proof',
    'discharge_summary',
    'hospital_bills',
    'diagnostic_reports',
  ],
  pre_auth: ['claim_form', 'policy_copy', 'id_proof', 'pre_auth_form'],
  death: ['claim_form', 'policy_copy', 'id_proof'],
  disability: ['claim_form', 'policy_copy', 'id_proof', 'diagnostic_reports'],
  accidental: ['claim_form', 'policy_copy', 'id_proof', 'fir_copy'],
};

export type ChecklistStatus = 'missing' | 'uploaded' | 'approved' | 'rejected';

/**
 * DQ-346: claim types whose checklist is SERVED (`ClaimDocumentRequirement`),
 * so the client list above is not used for them. Other types keep it until
 * their own server lists exist.
 */
export const SERVER_CHECKLIST_CLAIM_TYPES = ['cashless', 'reimbursement'];

export type ServerChecklistStatus = 'missing' | 'uploaded' | 'approved' | 'rejected' | 'resubmit';
export type RequirementLevel = 'mandatory' | 'optional' | 'conditional';

/** One row of `GET /claims/{id}/document-checklist/`. */
export interface ServerChecklistItem {
  document_type: string;
  label: string;
  help_text: string;
  requirement: RequirementLevel;
  is_mandatory: boolean;
  condition_text: string;
  status: ServerChecklistStatus;
  document_id: string | null;
  document_name: string;
  rejection_reason: string;
  uploaded_count: number;
}

export interface ServerChecklist {
  claim_type: string;
  supported: boolean;
  items: ServerChecklistItem[];
  complete: boolean;
  mandatory_missing: number;
  other_uploaded: { document_id: string; document_type: string; name: string; status: string }[];
  upload_allowed: boolean;
  upload_blocked_reason: string;
  note: string;
}

export interface DocumentRequirements {
  claim_type: string;
  items: Omit<ServerChecklistItem, 'status' | 'document_id' | 'document_name' | 'rejection_reason' | 'uploaded_count'>[];
  note: string;
}

export const claimChecklistApi = {
  async get(claimId: string): Promise<ServerChecklist> {
    const response = await api.get<ServerChecklist>(`/claims/${claimId}/document-checklist/`);
    return response.data;
  },

  /** The checklist for a claim type before the claim exists (claim form). */
  async requirements(claimType: string): Promise<DocumentRequirements> {
    const response = await api.get<DocumentRequirements>('/claims/document-requirements/', {
      params: { line: 'health', claim_type: claimType },
    });
    return response.data;
  },
};

export interface ChecklistEntry {
  code: string;
  label: string;
  status: ChecklistStatus;
  document?: ClaimDocumentRow;
  reason: string;
}

/**
 * Where each required document stands, judged from the NEWEST upload of that
 * type.
 *
 * Newest-wins is what makes a rejection actionable: a policyholder whose RC
 * copy came back "unreadable" sends a second one, and the checklist has to
 * follow the new file instead of staying stuck on the rejected one.
 */
export function buildChecklist(claimType: string, documents: ClaimDocumentRow[]): ChecklistEntry[] {
  const newestByType = new Map<string, ClaimDocumentRow>();
  for (const doc of documents) {
    const current = newestByType.get(doc.document_type);
    if (!current || doc.created_at > current.created_at) {
      newestByType.set(doc.document_type, doc);
    }
  }

  return (REQUIRED_DOCUMENTS_BY_CLAIM_TYPE[claimType] ?? []).map((code) => {
    const document = newestByType.get(code);
    if (!document) {
      return { code, label: documentTypeLabel(code), status: 'missing' as const, reason: '' };
    }
    const status: ChecklistStatus =
      document.status === 'approved'
        ? 'approved'
        : document.status === 'rejected' || document.status === 'resubmit'
          ? 'rejected'
          : 'uploaded';
    return {
      code,
      label: documentTypeLabel(code),
      status,
      document,
      reason: document.rejection_reason ?? '',
    };
  });
}

/**
 * Accept either the paginated envelope the API really sends or a bare array.
 *
 * Anything else (a string, `null`, an error body that slipped through) returns
 * an empty list — but callers must not read that as "this claim has no
 * documents": react-query surfaces the request error separately, and the panel
 * shows the error rather than an empty state.
 */
export function readList<T>(payload: unknown): T[] {
  if (Array.isArray(payload)) return payload as T[];
  if (payload && typeof payload === 'object' && Array.isArray((payload as { results?: unknown }).results)) {
    return (payload as { results: T[] }).results;
  }
  return [];
}

const base = (claimId: string) => `/claims/${claimId}/documents/`;

export const claimDocumentsApi = {
  async list(claimId: string): Promise<ClaimDocumentRow[]> {
    const response = await api.get(base(claimId));
    return readList<ClaimDocumentRow>(response.data);
  },

  /**
   * `document_type` and the bytes; `name` only when the user typed one.
   *
   * `file_path`, `file_size` and `mime_type` are deliberately NOT sent — the
   * serializer marks them read-only and the view derives them from the stored
   * upload. Sending a `file_path` is how the pre-fix rows came to point at
   * files that did not exist.
   */
  async upload(
    claimId: string,
    input: { file: File; documentType: string; name?: string }
  ): Promise<ClaimDocumentRow> {
    const form = new FormData();
    form.append('file', input.file);
    form.append('document_type', input.documentType);
    if (input.name?.trim()) form.append('name', input.name.trim());
    const response = await api.post<ClaimDocumentRow>(base(claimId), form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  async remove(claimId: string, documentId: string): Promise<void> {
    await api.delete(`${base(claimId)}${documentId}/`);
  },

  async verify(claimId: string, documentId: string): Promise<ClaimDocumentRow> {
    const response = await api.post<{ document: ClaimDocumentRow }>(
      `${base(claimId)}${documentId}/verify/`,
      {}
    );
    return response.data.document;
  },

  async reject(claimId: string, documentId: string, reason: string): Promise<ClaimDocumentRow> {
    const response = await api.post<{ document: ClaimDocumentRow }>(
      `${base(claimId)}${documentId}/reject/`,
      { rejection_reason: reason }
    );
    return response.data.document;
  },

  /**
   * Pull the stored bytes through the authenticated route and hand them to the
   * browser.
   *
   * `file_path` is a storage path, not a URL — the old panel used it as an
   * `href`, which navigated to a 404 on the SPA. The endpoint answers with
   * `Content-Disposition: attachment` and `nosniff`, so this is a save, not an
   * in-page render.
   */
  async download(claimId: string, document: ClaimDocumentRow): Promise<void> {
    const response = await api.get(`${base(claimId)}${document.id}/download/`, {
      responseType: 'blob',
    });
    const url = window.URL.createObjectURL(response.data as Blob);
    const link = window.document.createElement('a');
    link.href = url;
    link.download = document.name || 'claim-document';
    window.document.body.appendChild(link);
    link.click();
    window.document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  },
};
